import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BloodPressureDialog = ({ isOpen, onClose, patientId, onUpdate }) => {
  const [bpForm, setBpForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    time: format(new Date(), 'HH:mm'),
    systolic: '',
    diastolic: '',
    pulse: '',
    notes: '',
    image_url: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    
    setUploading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', imageFile);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setUploading(false);
      return response.data.image_url;
    } catch (error) {
      setUploading(false);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      // Upload image first if one was selected
      let imageUrl = '';
      if (imageFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          toast.error('Failed to upload image. Please try again.');
          return;
        }
      }

      await axios.post(
        `${API}/blood-pressure`,
        {
          patient_id: patientId,
          date: bpForm.date,
          time: bpForm.time,
          systolic: parseInt(bpForm.systolic),
          diastolic: parseInt(bpForm.diastolic),
          pulse: bpForm.pulse ? parseInt(bpForm.pulse) : null,
          notes: bpForm.notes,
          image_url: imageUrl,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Blood pressure recorded');
      onClose();
      setBpForm({
        date: format(new Date(), 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm'),
        systolic: '',
        diastolic: '',
        pulse: '',
        notes: '',
        image_url: '',
      });
      setImageFile(null);
      setImagePreview(null);
      onUpdate();
    } catch (error) {
      toast.error('Failed to record blood pressure');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Blood Pressure</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="bp-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                data-testid="bp-date-input"
                type="date"
                value={bpForm.date}
                onChange={(e) => setBpForm({ ...bpForm, date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Time</label>
              <input
                data-testid="bp-time-input"
                type="time"
                value={bpForm.time}
                onChange={(e) => setBpForm({ ...bpForm, time: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Systolic (mmHg) *</label>
              <input
                data-testid="bp-systolic-input"
                type="number"
                value={bpForm.systolic}
                onChange={(e) => setBpForm({ ...bpForm, systolic: e.target.value })}
                required
                placeholder="120"
                min="60"
                max="250"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Diastolic (mmHg) *</label>
              <input
                data-testid="bp-diastolic-input"
                type="number"
                value={bpForm.diastolic}
                onChange={(e) => setBpForm({ ...bpForm, diastolic: e.target.value })}
                required
                placeholder="80"
                min="40"
                max="150"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Pulse (bpm)</label>
            <input
              data-testid="bp-pulse-input"
              type="number"
              value={bpForm.pulse}
              onChange={(e) => setBpForm({ ...bpForm, pulse: e.target.value })}
              placeholder="72"
              min="40"
              max="200"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes (optional)</label>
            <textarea
              data-testid="bp-notes-input"
              value={bpForm.notes}
              onChange={(e) => setBpForm({ ...bpForm, notes: e.target.value })}
              rows="3"
              placeholder="Any additional notes..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Attach Image (optional)
            </label>
            <div className="flex gap-2">
              <label className="btn text-sm px-4 py-2 cursor-pointer flex items-center gap-2" style={{ backgroundColor: '#A2DAD3', color: 'white' }}>
                📷 Camera
                <input
                  data-testid="bp-camera-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
              <label className="btn text-sm px-4 py-2 cursor-pointer flex items-center gap-2" style={{ backgroundColor: '#A2DAD3', color: 'white' }}>
                📁 Upload
                <input
                  data-testid="bp-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
            {imagePreview && (
              <div className="mt-2 flex items-center gap-2">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="h-32 object-contain border rounded cursor-pointer"
                  onClick={() => window.open(imagePreview, '_blank')}
                  title="Click to view full size"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImagePreview(null);
                    setImageFile(null);
                  }}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  ✕ Remove
                </button>
              </div>
            )}
          </div>

          <button
            data-testid="submit-bp-button"
            type="submit"
            className="btn btn-primary w-full"
            disabled={uploading}
          >
            {uploading ? 'Uploading Image...' : 'Record Blood Pressure'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BloodPressureDialog;
